/**
 * RentListContainer
 * --
 */
import React, { useState } from 'react';
// import ShippingList from 'routes/admin.routes/admin.orders/ShippingList';
import Presenter from './RentListPresenter';
import MessageAlert, { POINT_STORE_ID } from 'utils';
import dayjs from 'dayjs';
import {
  useGetRentals,
  rentalQueryKeys,
  useBulkUpdateRental,
} from 'hooks/RentalHooks';
import { useQueryClient } from '@tanstack/react-query';
import { useCreateRefundRentalManual } from 'hooks/RefundHooks';
import {
  usePaymentCancelAccount,
  usePaymentCancelCard,
} from 'hooks/PaymentHooks.';

const today = {
  start_date: dayjs().subtract(7, 'day').startOf('day').unix(),
  end_date: dayjs().endOf('day').unix(),
  is_active: 'ALL',
};
const initialSearchFilter = {
  keywordType: 'shop_order_id',
  keyword: '',
  isActive: 'ALL',
  dateType: 'week', // [today, week, month, 3month]
  getType: 'PAYMENT',
  startDate: null,
  endDate: null,
  start_date: today.start_date,
  end_date: today.end_date,
  ///
  keywordValue: '',
};

const options = {
  gcTime: Infinity,
  select: (res) => {
    const { status, data } = res;

    if (status !== 200) return [];
    else return data;
  },
  initialData: [],
};

/**----------------------------------------
 * [Component] 배송내역 - 컨테이너
 * ----------------------------------------
 */
const RentListContainer = ({ defaultShopId, permission, type }) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  // const [orderList, setOrderList] = useState([]);
  const [period, setPeriod] = useState(today);
  const [searchFilter, setSearchFilter] = useState(initialSearchFilter);
  const [currentShop, setCurrentShop] = useState(defaultShopId ?? null);

  /* ===== VARIABLE ===== */
  /* ===== MUTATE ===== */
  const { cancelCard } = usePaymentCancelCard();
  const { cancelAccount } = usePaymentCancelAccount(); // 계좌이체 취소
  const { createRefundRentalManual } = useCreateRefundRentalManual();
  const { bulkUpdateRental } = useBulkUpdateRental();

  const refundMutate = {
    RENTAL: createRefundRentalManual,
  };
  /* ===== VARIABLE ===== */
  const { data: rentals } = useGetRentals(
    {
      type: 'RENTAL',
      ...(currentShop && { shop_information_id: currentShop }),
      join_tables: `["user", "shop_information", "shop_order", "refund"]`,
      // start_date: period.start_date,
      // end_date: period.end_date,
      ...(period.is_active !== 'ALL' && { is_active: period.is_active }),
    },
    {
      ...options,
      enabled: defaultShopId ? !!currentShop : true,
    }
  );
  /* ===== FUNCTION ===== */
  /**
   * 데이터 정제
   * --
   */
  const rentalList = React.useMemo(() => {
    setLoading(true);
    const { start_date, end_date, get_type } = period;

    try {
      let data = rentals
        ?.map((rental) => {
          let rentalPaymentInfo = rental?.rental_payment_info;
          //날짜 필터
          if (get_type === 'PAYMENT') {
            rentalPaymentInfo = rental?.rental_payment_info?.filter((rpi) => {
              return (
                rpi.payment_date >= start_date && rpi.payment_date <= end_date
              );
            });
          }
          return {
            key: rental.rental_id,
            ...rental,
            rental_payment_info: rentalPaymentInfo,
          };
        })
        .filter((o) => {
          //날짜 필터
          if (get_type === 'CREATE') {
            return (
              o.created_at >= start_date &&
              o.created_at <= end_date &&
              (currentShop ? o.shop_information_id === currentShop : true)
            );
          }
          return o.rental_payment_info?.length;
        })
        ?.sort((a, b) => b.rental_id - a.rental_id);

      ///
      const { keyword, keywordType } = searchFilter;
      const filteredCall = (o) => `${o[keywordType]}`.indexOf(keyword) >= 0;
      if (keywordType === 'shop_order_id') {
        data = data.filter(filteredCall);
      } else if (keywordType === 'product_name') {
        data = data.filter((o) => {
          return filteredCall(o['product']);
        });
      } else if (keywordType === 'user_name') {
        data = data.filter((o) => filteredCall(o['user']));
      }
      setLoading(false);
      return data;
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 2500);
    }
  }, [rentals, searchFilter.keyword, period, currentShop]);
  /**--------------------
   * 필터
   * --------------------
   */
  const handleChange = (key, value) => {
    setSearchFilter((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  /**
   * 검색
   * --
   */
  const handleSearch = async (shopIdx = defaultShopId) => {
    // if (!currentShop) {
    //   return MessageAlert.info('매장을 선택해주세요.');
    // }

    const { dateType, startDate, endDate, keywordValue, isActive, getType } =
      searchFilter;

    const dd =
      dateType === 'today'
        ? 0
        : dateType === 'week'
        ? -7
        : dateType === 'month'
        ? -30
        : -90;
    setSearchFilter({
      ...searchFilter,
      keyword: keywordValue,
    });
    setCurrentShop(shopIdx);
    if (startDate && endDate) {
      setPeriod({
        start_date: startDate,
        end_date: endDate,
        get_type: getType,
        is_active: isActive,
      });
    } else {
      setPeriod({
        start_date: dayjs().add(dd, 'days').startOf('day').unix(),
        end_date: dayjs().endOf('day').unix(),
        get_type: getType,
        is_active: isActive,
      });
    }
  };
  /**
   * 검색 핌터 초기화
   * --
   */
  const handleResetFilter = () => {
    setPeriod(today);
    setSearchFilter(initialSearchFilter);
    setCurrentShop(defaultShopId);
  };

  /**
   * 데이터 최신화
   * --
   */
  const handleReload = () => {
    const queryKey = rentalQueryKeys.getRentals({
      type: 'RENTAL',
      ...(currentShop && { shop_information_id: currentShop }),
      join_tables: `["user", "shop_information", "shop_order", "refund"]`,
      // start_date: period.start_date,
      // end_date: period.end_date,
      ...(period.is_active !== 'ALL' && { is_active: period.is_active }),
    });
    queryClient.invalidateQueries({ queryKey: queryKey.queryKey });
  };
  /**--------------------
   * 전체 주문내역 조회
   * --------------------
   */

  // const getOrders = async (f = null) => {
  //   setLoading(true);
  //   try {
  //     const { status, message, data } = await RentalApi.getRentals({
  //       // start_date: today.start_date,
  //       // end_date: today.end_date,
  //       ...(currentShop && { shop_information_id: currentShop }),
  //       join_tables: `["user", "shop_information", "shop_order", "refund"]`,
  //     });
  //     if (status !== 200) throw { message };
  //     console.log('[RentListContainer][getOrders]data: ', data);

  //     setTimeout(() => {
  //       setOrderList(data);
  //       setLoading(false);
  //     }, 750);
  //   } catch (e) {
  //     setTimeout(() => {
  //       setLoading(false);
  //       MessageAlert.error(e.message);
  //     }, 2500);
  //   }
  // };

  // /* ===== HOOKS ===== */
  // /*  */
  // useEffect(() => {
  //   currentShop && getOrders();
  // }, [currentShop]);

  /**
   * 운송장 택배 저장
   * --
   */
  const handleSaveDeliveryInfo = async (data, callback = null) => {
    try {
      const { status } = await bulkUpdateRental([data]);
      if (status === 200) {
        // NOTE: 데이터 최신화
        handleReload();

        MessageAlert.success('택배사/송장번호 등록 되었습니다.');
        callback && callback();
      }
    } catch (e) {
      MessageAlert.error('택배사/송장번호 등록 중 오류가 발생하였습니다!');
    }
  };

  /**--------------------
   * 주문 취소
   * --------------------
   * @param {object} refund 환불 정보
   * @param {string} type 결제 수단
   */
  const handleCancel = async (refund, type = 'CARD') => {
    /**NOTE - refund
       * trno[string]: payment_info의 trno (JM_PAY가 아닐 경우 필수)
       * cancelTaxFreeAmount[string | number]: 취소 면세금액
       * 예시) 총금액 1만원 부분취소 요청
              과세상품인 경우 refund_amount=10000, cancelTaxFreeAmount=0
              면세상품인 경우 refund_amount=10000, cancelTaxFreeAmount=10000
              일부(3천원) 면세상품인 경우 refund_amount=10000, cancelTaxFreeAmount=3000
       * cancelSeq: 취소 순서 (최초 1) refund가 일어난 횟수 + 1 (JM_PAY가 아닐 경우 필수)
       *
       * refund_amount[number]: 환불금액 (필수)
       * refund_num[number]: 환불수량 (필수)
       * is_coupon_refund[string]: 쿠폰환불 여부 (필수)
       * refund_reason[string]: 환불사유
       *
       * parcel_service_id[number]: 배송, 정기배송일 경우 필수
       * regular_payment_id: 정기배송일 경우 필수
       * rental_id: 렌탈일 경우 필수
       * product_type: DELIVERY | SUBSCRIPTION | RENTAL
       */
    let success = false;
    try {
      setLoading(true);
      const paymentType = type.toLocaleUpperCase();

      const {
        trno,
        cancelTaxFreeAmount,
        cancelSeq,
        product_type,
        refund_amount,
        ...rest
      } = refund;

      const cancelData = {
        mid: POINT_STORE_ID,
        orgTradeKeyType: 'TID',
        orgTradeKey: trno,
        cancelType: 'PARTIAL',
        cancelTotalAmount: `${refund_amount}`,
        cancelTaxFreeAmount: `${cancelTaxFreeAmount ?? 0}`,
        cancelSeq: `${cancelSeq ?? 1}`,
      };

      const refundData = {
        ...rest,
        memo: rest?.refund_reason,
        refund_amount,
        refund_status: 'COMPLETED',
      };

      let cancelled, refunded;
      switch (paymentType) {
        case 'MOBILE': // 휴대폰 결제
          MessageAlert.warning('휴대폰 결제는 취소 불가능합니다!');
          return false;
        case 'TRANSFER': // TODO: 계좌이체 (테스트 필요)
          // 계좌이체 결제 취소
          cancelled = await cancelAccount(cancelData);

          if (cancelled.status !== 200 || cancelled.data.code !== 'A0200') {
            MessageAlert.info('결제 취소에 실패하였습니다.');
            return false;
          }

          // 환불 데이터 생성
          refunded = await refundMutate[product_type]({
            ...refundData,
            refund_payment_info: cancelled.data.data,
          });
          break;
        case 'JM_PAY': // 제이엠페이 (테스트 필요)
          // JM페이 환불
          // const { status } = await refundChargeJmPay({
          //   user_id: rest?.user_id,
          //   price: refund_amount,
          // });

          // if (status !== 200) {
          //   MessageAlert.info('JM Pay 환불에 실패하였습니다.');
          //   return;
          // }

          // 환불 데이터 생성
          refunded = await refundMutate[product_type]({
            ...refundData,
            refund_payment_info: null,
          });

          break;
        case 'CARD':
        case 'NAVER_PAY':
        case 'KAKAO_PAY':
          // 카드 결제 취소
          cancelled = await cancelCard(cancelData);

          if (cancelled.status !== 200 || cancelled.data.code !== 'A0200') {
            MessageAlert.info('결제 취소에 실패하였습니다.');
            return false;
          }

          // 환불 데이터 생성
          refunded = await refundMutate[product_type]({
            ...refundData,
            refund_payment_info: cancelled.data.data,
          });

          break;
        default:
          MessageAlert.warning('알 수 없는 결제 타입!');
          return false;
      }

      if (refunded.status !== 200) {
        MessageAlert.info('환불 데이터 생성에 실패하였습니다.');
        return false;
      }

      MessageAlert.success('결제가 성공적으로 취소되었습니다.');
      success = true;
      return success;
    } catch (err) {
      console.error('[handleCancel] Error!! ', err);
      MessageAlert.error('결제 취소에 실패하였습니다.');
    } finally {
      setTimeout(() => {
        setLoading(false);
        return success;
      }, 1000);
    }
  };

  /* ===== RENDER ===== */
  return (
    <>
      <Presenter
        {...{
          loading,
          permission,
          type: 'RENTAL',
          rentalList,
          setCurrentShop,
          searchFilter,
          currentShop,
          //
          onChange: handleChange,
          onSearch: handleSearch,
          onReload: handleReload,
          onSaveDeliveryInfo: handleSaveDeliveryInfo,
          onResetFilter: handleResetFilter,
          onCancel: handleCancel,
        }}
      />
    </>
  );
};

/*  */
RentListContainer.defaultProps = {
  defaultShopId: null,
  type: null,
  permission: 'ADMIN',
};
export default RentListContainer;
